/**
 * Runtime configuration — loaded from /config.json BEFORE React renders.
 *
 * A single build artifact works in every environment: ops swap config.json,
 * never rebuild. No import.meta.env for environment-specific values.
 */

export interface AppConfig {
  apiBaseUrl: string;
  wsUrl: string;
  keycloak: {
    authority: string;
    clientId: string;
    /**
     * The Karexia client whose `resource_access.<roleClientId>.roles` carry the
     * Karexia roles (shared `core` realm: Karexia roles are CLIENT roles, never
     * realm roles). Defaults to "pubsocialnet-backend" when absent.
     */
    roleClientId?: string;
    /**
     * Issuers (realm URLs) whose `realm_access.roles` are still honoured — the
     * legacy dedicated Karexia realm during the cutover. Tokens from any other
     * issuer use only the role client's roles. Defaults to [] (fail closed).
     */
    legacyIssuers?: string[];
  };
  /** Optional environment label surfaced in the shell footer (e.g. "prod", "staging"). */
  env?: string;
  /** Set to "admin" on the dedicated global-admin host (admin.karexia.com). */
  portal?: 'admin';
  /**
   * Origin of the dedicated global-admin host (e.g. https://admin.karexia.com).
   * When set on the tenant app, `/admin/*` redirects there so the console is
   * served only from its own origin. Unset in envs without an admin host.
   */
  adminPortalUrl?: string;
  /** Origin of the tenant app (e.g. https://www.karexia.com), used by the admin host. */
  appUrl?: string;
}

let config: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (config) return config;
  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: ${response.status}`);
  }
  config = (await response.json()) as AppConfig;
  return config;
}

export function getConfig(): AppConfig {
  if (!config) {
    throw new Error('Config not loaded — loadConfig() must run before render');
  }
  return config;
}
